import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { authApi } from '@/lib/auth-api';
import { getErrorMessage } from '@/lib/errors';

type State = { status: 'verifying' } | { status: 'ok' } | { status: 'error'; message: string };

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>({ status: 'verifying' });
  // StrictMode double-invokes effects in dev; the token is single-use, so guard it.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setState({ status: 'error', message: 'This verification link is missing its token.' });
      return;
    }

    authApi
      .verifyEmail(token)
      .then(() => setState({ status: 'ok' }))
      .catch((err: unknown) => setState({ status: 'error', message: getErrorMessage(err) }));
  }, [token]);

  return (
    <AuthLayout title="Email verification">
      <div className="flex flex-col items-center gap-3 text-center">
        {state.status === 'verifying' && (
          <>
            <Loader2 className="size-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Verifying your email address…</p>
          </>
        )}

        {state.status === 'ok' && (
          <>
            <CheckCircle2 className="size-10 text-success" />
            <p className="text-sm text-muted-foreground">
              Your email is verified and your account is active.
            </p>
            <Button asChild className="mt-2 w-full">
              <Link to="/login">Sign in</Link>
            </Button>
          </>
        )}

        {state.status === 'error' && (
          <>
            <XCircle className="size-10 text-danger" />
            <p className="text-sm text-muted-foreground">{state.message}</p>
            <Button asChild variant="outline" className="mt-2 w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
